import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { createAccount } from '../api/apiService'
import { ToastSuccess, ToastError } from "../components/toast-notification";

function CreateAccount() {
  const navigate = useNavigate();
  const xDeviceId = localStorage.getItem("x-device-service-id");
  const [formValues, setFormValues] = useState({ fullName: '', email: '', password: '', mobile: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'fullName') {
      if (!value) error = 'Full Name is required';
      else if (value.length < 5) error = 'Full Name must be at least 5 characters';
    }
    if (name === 'email') {
      if (!value) error = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(value)) error = 'Email address is invalid';
    }
    if (name === 'password') {
      if (!value) error = 'Password is required';
      else if (value.length < 6) error = 'Password must be at least 6 characters';
    }
    if (error) setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    let errors = {};
    if (!formValues.fullName) errors.fullName = 'Full Name is required';
    else if (formValues.fullName.length < 5) errors.fullName = 'Full Name must be at least 5 characters';
    if (!formValues.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formValues.email)) errors.email = 'Email address is invalid';
    if (!formValues.password) errors.password = 'Password is required';
    else if (formValues.password.length < 6) errors.password = 'Password must be at least 6 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const trySignup = (bodyData) => {
    bodyData.signupUsing = "email";
    createAccount(bodyData)
      .then(r => { ToastSuccess(r?.data?.message); setIsLoading(false); navigate('/login'); })
      .catch(e => { setIsLoading(false); ToastError(e?.response?.data?.message); });
  };

  const googleLogin = (e) => {
    e.preventDefault();
    const url = `${process.env.REACT_APP_BASE_URL}/social-login/google`;
    window.location.href = `${url}?appName=${process.env.REACT_APP_NAME}&fromUrl=${window.location.origin}/authorize-callback&deviceId=${xDeviceId}`;
  };

  const githubLogin = (e) => {
    e.preventDefault();
    const url = `${process.env.REACT_APP_BASE_URL}/social-login/github`;
    window.location.href = `${url}?appName=${process.env.REACT_APP_NAME}&fromUrl=${window.location.origin}/authorize-callback&deviceId=${xDeviceId}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) { setIsLoading(true); trySignup(formValues); }
  };

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#030305]">
      
      {/* LEFT PANEL: Cyberpunk Visuals */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12 border-r border-fuchsia-900/30">
        <div className="absolute inset-0 cyber-auth-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#030305]" />
        
        <div className="relative z-10 flex items-center gap-4 mb-auto">
          <div className="w-10 h-10 border border-fuchsia-500 bg-fuchsia-900/30 flex items-center justify-center" style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
            <svg className="w-5 h-5 text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <span className="text-2xl font-black text-fuchsia-400 tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,0,255,0.4)]">
            EXTENDSWARE
          </span>
        </div>

        <div className="relative z-10 my-auto">
          <h2 className="text-[4rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-fuchsia-500 to-cyan-400 drop-shadow-[0_0_15px_rgba(255,0,255,0.3)]">
            NODE
            <br />
            REGISTRATION
          </h2>
          <div className="mt-6 flex flex-col gap-2 font-mono text-fuchsia-500/60 text-xs">
            <p>> ALLOCATING NEW DATA SECTOR...</p>
            <p>> GENERATING ENCRYPTION KEYS... [OK]</p>
            <p>> AWAITING OPERATOR INPUT...</p>
            <div className="h-4 w-32 bg-fuchsia-500/20 border border-fuchsia-500/30 mt-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-2/3 bg-fuchsia-500/50 animate-pulse" />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-auto flex items-center justify-between text-[10px] font-mono text-fuchsia-900">
          <span>SYS.VER: 9.4.2</span>
          <span>ENCRYPTION: 256-BIT NEURAL</span>
        </div>
      </div>

      {/* RIGHT PANEL: Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-fuchsia-900/10 via-[#030305] to-[#030305] lg:hidden" />
        
        <div className="w-full max-w-md cyber-card p-8 md:p-10 relative z-10 shadow-none border-none lg:bg-transparent lg:backdrop-filter-none">
          
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-2xl font-black text-white tracking-widest uppercase drop-shadow-[0_0_8px_rgba(255,255,255,0.3)] mb-2">
              SYS.REGISTER_NODE
            </h1>
            <p className="cyber-label text-[10px]">
              INITIALIZE NEW OPERATOR PROFILE IN THE MAINFRAME.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="cyber-label block mb-2">OPERATOR_DESIGNATION [NAME]</label>
              <input id="fullName" name="fullName" type="text"
                className={`cyber-input w-full p-3 font-mono ${formErrors.fullName ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="J. Doe" value={formValues.fullName} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.fullName && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.fullName}
              </p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="cyber-label block mb-2">IDENTIFIER [EMAIL]</label>
              <input id="email" name="email" type="email" autoComplete="email"
                className={`cyber-input w-full p-3 font-mono ${formErrors.email ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="operator@sys.net" value={formValues.email} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.email && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.email}
              </p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="cyber-label block mb-2">SECURITY_KEY [PASSWORD]</label>
              <input id="password" name="password" type="password"
                className={`cyber-input w-full p-3 font-mono ${formErrors.password ? "border-red-500 shadow-[0_0_10px_rgba(255,0,0,0.3)]" : ""}`}
                placeholder="********" value={formValues.password} onChange={handleChange} onBlur={handleBlur} />
              {formErrors.password && <p className="mt-2 text-[10px] font-bold text-red-500 uppercase tracking-widest">
                [!] {formErrors.password}
              </p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading}
              className="cyber-btn cyber-btn-magenta w-full py-4 mt-2">
              {isLoading ? "INITIALIZING..." : "EXECUTE_REGISTRATION"}
            </button>
          </form>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-cyan-900/50" />
            <span className="cyber-label text-[9px]">OR EXTERNAL UPLINK</span>
            <div className="flex-1 h-px bg-cyan-900/50" />
          </div>

          {/* Social */}
          <div className="grid grid-cols-2 gap-4">
            <button onClick={googleLogin} className="cyber-btn py-3 text-xs flex items-center justify-center gap-2 border-cyan-800 text-cyan-500 hover:border-cyan-400 hover:text-cyan-400">
              GGL_AUTH
            </button>
            <button onClick={githubLogin} className="cyber-btn py-3 text-xs flex items-center justify-center gap-2 border-cyan-800 text-cyan-500 hover:border-cyan-400 hover:text-cyan-400">
              GHB_AUTH
            </button>
          </div>

          <p className="mt-8 text-center text-[10px] font-bold uppercase tracking-widest text-gray-500">
            ALREADY AUTHORIZED?{" "}
            <a href="/login" className="text-cyan-400 hover:text-cyan-300 drop-shadow-[0_0_5px_rgba(0,255,255,0.4)] ml-1">RETURN_TO_LOGIN</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreateAccount;
